/**
 * Indicateur de quota restant
 * Affiche le nombre de recherches gratuites restantes
 */
import { useAuthStore } from '../../stores/authStore'

interface QuotaIndicatorProps {
  variant?: 'badge' | 'inline' | 'full'
}

export default function QuotaIndicator({ variant = 'badge' }: QuotaIndicatorProps) {
  const { quotaRemaining, isAuthenticated, user, openLoginModal } = useAuthStore()

  // Utilisateur abonné = pas de limite
  if (user?.isSubscribed) {
    if (variant === 'badge') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          Illimité
        </span>
      )
    }
    return null
  }

  // Couleur selon le quota restant
  let colorClass = 'bg-green-100 text-green-800'
  if (quotaRemaining <= 1) {
    colorClass = 'bg-red-100 text-red-800'
  } else if (quotaRemaining <= 2) {
    colorClass = 'bg-amber-100 text-amber-800'
  }

  if (variant === 'badge') {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {quotaRemaining} recherche{quotaRemaining > 1 ? 's' : ''} restante{quotaRemaining > 1 ? 's' : ''}
      </span>
    )
  }

  if (variant === 'inline') {
    return (
      <span className={`text-sm ${quotaRemaining <= 1 ? 'text-red-600' : 'text-gray-600'}`}>
        {quotaRemaining}/3 recherches gratuites
      </span>
    )
  }

  // Variant full - avec CTA
  return (
    <div className={`flex items-center gap-3 px-4 py-2 rounded-lg ${quotaRemaining <= 1 ? 'bg-red-50' : 'bg-gray-50'}`}>
      <div className="flex items-center gap-2">
        {/* Progress dots */}
        <div className="flex gap-1">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full ${
                i <= quotaRemaining
                  ? quotaRemaining <= 1
                    ? 'bg-red-500'
                    : 'bg-green-500'
                  : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
        <span className={`text-sm font-medium ${quotaRemaining <= 1 ? 'text-red-800' : 'text-gray-700'}`}>
          {quotaRemaining} recherche{quotaRemaining > 1 ? 's' : ''} gratuite{quotaRemaining > 1 ? 's' : ''}
        </span>
      </div>

      {!isAuthenticated && (
        <button
          onClick={openLoginModal}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          Se connecter
        </button>
      )}
    </div>
  )
}
